import { useEffect, useRef } from 'react';

/** Everything the browser will let a person Tab to. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * KEEP A KEYBOARD INSIDE A DIALOG, AND GIVE IT A WAY OUT.
 *
 * These dialogs are drawn last in the tree, after a list that on a feature is a
 * hundred and twenty scene cards with a couple of thousand controls between
 * them. Without this, "Merge" opened an overlay while focus stayed on the
 * button behind it, Tab walked into the cards underneath, and Escape did
 * nothing — so a producer working by keyboard could neither reach the dialog
 * nor leave it, on a screen whose whole purpose is corrections.
 *
 * THREE THINGS, because any one alone is still a trap:
 *
 * ON OPEN, focus moves to the first control inside — the dialog's own question,
 * which is where the person's attention already is.
 *
 * WHILE OPEN, Tab and Shift+Tab wrap at the ends rather than escaping into the
 * page behind, and Escape closes.
 *
 * ON CLOSE, focus returns to whatever opened the dialog, so the producer is put
 * back where they were rather than at the top of the document.
 *
 * `aria-modal` is a promise to assistive technology that the rest of the page
 * is inert. This is what makes the promise true.
 */
export function useModalFocus<T extends HTMLElement>(onClose: () => void) {
  const ref = useRef<T | null>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    opener.current = document.activeElement;
    const node = ref.current;
    if (node === null) return;

    // A dialog may name where the person starts (`data-autofocus`): the email
    // field of a form rather than the Close button drawn above it.
    const first = node.querySelector<HTMLElement>('[data-autofocus]') ?? node.querySelector<HTMLElement>(FOCUSABLE);
    // The container itself when it holds nothing focusable — a confirmation
    // with its buttons still rendering, say. Never nothing.
    (first ?? node).focus?.();

    return () => {
      const back = opener.current;
      if (back instanceof HTMLElement && back.isConnected) back.focus();
    };
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<T>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();

      return;
    }

    if (event.key !== 'Tab') return;

    const node = ref.current;
    if (node === null) return;

    const focusable = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      // `offsetParent` is null for anything display:none — a control inside a
      // collapsed section is in the DOM and not reachable, and tabbing to it
      // would look like focus vanishing.
      (el) => el.offsetParent !== null || el === document.activeElement,
    );

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return { ref, onKeyDown };
}
